import axios from 'axios';

const JUNO_SERVER = process.env.REACT_APP_JUNO_SERVER;

/**
 * Helper to get template configuration from localStorage
 * Throws error if configuration is not found
 */
const getTemplateConfig = () => {
  const saved = localStorage.getItem('templateConfig');
  if (!saved) {
    throw new Error('Template configuration not found. Please configure templates in Settings first.');
  }
  try {
    const config = JSON.parse(saved);
    // Validate that all required template IDs and access token are present
    if (!config.login_template_id || !config.pep_template_id || !config.age_template_id || !config.access_token) {
      throw new Error('Incomplete template configuration. Please configure all template IDs and access token in Settings.');
    }
    return config;
  } catch (e) {
    if (e.message.includes('configuration')) {
      throw e; // Re-throw our custom errors
    }
    throw new Error('Invalid template configuration. Please reconfigure in Settings.');
  }
};

/**
 * Register a user via Juno unified registration endpoint
 * This creates user accounts and issues credentials based on PEP status
 */
export const registerUser = async (userData) => {
  try {
    // Get template configuration
    const templateConfig = getTemplateConfig();

    const registrationData = {
      email: userData.email,
      password: userData.password,
      name: userData.firstName,
      lastName: userData.lastName,
      birthDate: userData.dateOfBirth,
      companyName: userData.companyName || `Company-${Date.now()}`,
      simulatePepHit: userData.createPEP || false,
      // Pass template IDs from configuration
      template_ids: {
        login: templateConfig.login_template_id,
        pep: templateConfig.pep_template_id,
        age: templateConfig.age_template_id
      },
      // Pass access token to identify admin's customer for EUDIPLO batch config
      access_token: templateConfig.access_token
    };

    const response = await axios.post(
      `${JUNO_SERVER}/papi/v3/unified-register`,
      registrationData,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.data.success) {
      return {
        success: true,
        data: response.data.data
      };
    } else {
      return {
        success: false,
        error: response.data.error || 'Registration failed'
      };
    }
  } catch (error) {
    console.error('Registration error:', error);
    // Check if it's a configuration error
    if (error.message && error.message.includes('configuration')) {
      return {
        success: false,
        error: error.message
      };
    }
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Network error'
    };
  }
};

/**
 * Generate QR code data from credential offer URI
 * This is what wallets will scan to receive credentials
 */
export const generateQRData = (offerUri) => {
  // The offer URI is already in the correct format for wallets
  // openid-credential-offer://?credential_offer=...
  return offerUri;
};

/**
 * Format credential type for display
 */
export const formatCredentialType = (type) => {
  const typeMap = {
    'LoginCredential': 'Login Credential',
    'NoPEPHitCredential': 'PEP Verification',
    'AgeCredential': 'Age Verification'
  };
  return typeMap[type] || type;
};

/**
 * Generate QR code data for login authentication
 * Calls Juno to create presentation request (which then calls Integration Service)
 */
export const generateLoginQRData = async () => {
  try {
    // Get template configuration
    const templateConfig = getTemplateConfig();

    // Call Juno public endpoint - no auth token needed!
    const response = await axios.post(
      `${JUNO_SERVER}/papi/v3/presentation-request`,
      {
        template_ids: [templateConfig.login_template_id], // LoginCredential template from config
        purpose: 'Login Authentication',
        response_type: 'uri'
      },
      {
        headers: {
          'Content-Type': 'application/json'
          // No X-Token needed - it's a public endpoint!
        }
      }
    );

    if (response.data.uri && response.data.session) {
      // Store session for polling
      sessionStorage.setItem('login_session', response.data.session);

      // Return the URI directly for QR code generation
      // The backend already generated the QR code as base64
      if (response.data.qrcode && response.data.qrcode.startsWith('data:image')) {
        // If backend provided base64 QR code, use it
        return response.data.qrcode;
      }

      // Otherwise return URI for client-side QR generation
      return response.data.uri;
    }

    throw new Error('Invalid response from presentation request');

  } catch (error) {
    console.error('Failed to generate login QR data:', error.response?.data || error.message);
    // Re-throw error to be handled by component
    throw error;
  }
};

/**
 * Poll for login presentation result
 * Checks if the wallet has submitted a valid credential via Juno proxy
 */
export const pollLoginResult = async (sessionId) => {
  try {
    const response = await axios.get(
      `${JUNO_SERVER}/papi/v3/presentation-result/${sessionId}`
    );
    
    if (response.data.status === 'verified') {
      return {
        success: true,
        verified: true,
        claims: response.data.extractedClaims,
        email: response.data.extractedClaims?.email,
        userId: response.data.claims?.user_id || response.data.claims?.id
      };
    } else if (response.data.status === 'pending') {
      return {
        success: false,
        pending: true
      };
    }
    
    return {
      success: false,
      error: 'Verification failed or expired'
    };
    
  } catch (error) {
    // 404 could mean session exists but result not yet ready - treat as pending
    if (error.response?.status === 404) {
      const errorMessage = error.response?.data?.error || '';
      if (errorMessage.includes('pending') || errorMessage.includes('not found')) {
        return {
          success: false,
          pending: true
        };
      }

      return {
        success: false,
        error: 'Session expired'
      };
    }

    console.error('Failed to poll login result:', error.response?.data || error.message);
    return {
      success: false,
      error: error.response?.data?.error || error.message
    };
  }
};